import { supabase } from '@sixam/supabase/client';
import type { Event, EventRsvp } from '@sixam/shared/events';
import {
  createBoundedEventPage,
  HOME_EVENT_PAGE_SIZE,
  type EventPage,
  type EventPageCursor,
  type HomeEventFilter,
} from '@sixam/shared/home-feed-core';

export type { Event, EventRsvp, EventStatus } from '@sixam/shared/events';
export { mapsUrl, staticMapUri } from '@/lib/event-location';

type EventDataInvalidationListener = () => void;

const eventDataInvalidationListeners = new Set<EventDataInvalidationListener>();

/** Refreshes mounted event lists after an event mutation completes. */
export function invalidateEventData(): void {
  for (const listener of eventDataInvalidationListeners) {
    listener();
  }
}

export function subscribeToEventDataInvalidation(
  listener: EventDataInvalidationListener
): () => void {
  eventDataInvalidationListeners.add(listener);
  return () => {
    eventDataInvalidationListeners.delete(listener);
  };
}

/** Location chosen via Google Places, stored inline on the event. */
export type PlaceSelection = {
  placeId: string | null;
  name: string;
  address: string;
  lat: number;
  lng: number;
};

export function spotsLeft(event: Pick<Event, 'capacity' | 'attendee_count'>): number | null {
  if (event.capacity == null) {
    return null;
  }
  return Math.max(0, event.capacity - event.attendee_count);
}

export function isEventFull(event: Pick<Event, 'capacity' | 'attendee_count'>): boolean {
  const left = spotsLeft(event);
  return left != null && left <= 0;
}

export function formatEventWhen(
  startsAt: string,
  endsAt: string | null,
  timeZone?: string
): string {
  const start = new Date(startsAt);
  const dateFormatter = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone,
  });
  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });

  const datePart = dateFormatter.format(start);
  const startTime = timeFormatter.format(start);

  if (!endsAt) {
    return `${datePart} · ${startTime}`;
  }

  return `${datePart} · ${startTime}–${timeFormatter.format(new Date(endsAt))}`;
}

export async function fetchPublishedEvents(tenantId?: string): Promise<Event[]> {
  let query = supabase
    .from('events')
    .select('*')
    .eq('status', 'published')
    .order('starts_at', { ascending: true });

  if (tenantId) {
    query = query.eq('tenant_id', tenantId);
  }

  const { data, error } = await query;
  if (error) {
    throw error;
  }
  return data ?? [];
}

export async function fetchPublishedEventsForTenants(
  tenantIds: readonly string[]
): Promise<Event[]> {
  if (tenantIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('status', 'published')
    .in('tenant_id', [...tenantIds])
    .order('starts_at', { ascending: true });

  if (error) {
    throw error;
  }
  return data ?? [];
}

export async function fetchPublishedEventPage({
  cursor = null,
  filter,
  limit = HOME_EVENT_PAGE_SIZE,
  now,
}: Readonly<{
  cursor?: EventPageCursor | null;
  filter: HomeEventFilter;
  limit?: number;
  now: Date;
}>): Promise<EventPage<Event>> {
  const ascending = filter === 'upcoming';
  let query = supabase.from('events').select('*').eq('status', 'published');
  query =
    filter === 'upcoming'
      ? query.gt('ends_at', now.toISOString())
      : query.lte('ends_at', now.toISOString());
  query = query
    .order('starts_at', { ascending })
    .order('id', { ascending })
    .limit(limit + 1);

  if (cursor) {
    const timeOperator = ascending ? 'gt' : 'lt';
    const idOperator = ascending ? 'gt' : 'lt';
    query = query.or(
      `starts_at.${timeOperator}.${cursor.startsAt},and(starts_at.eq.${cursor.startsAt},id.${idOperator}.${cursor.id})`
    );
  }

  const { data, error } = await query;
  if (error) throw error;
  return createBoundedEventPage(data ?? [], limit);
}

export async function fetchEventsByIds(eventIds: readonly string[]): Promise<Event[]> {
  if (eventIds.length === 0) return [];
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .in('id', [...eventIds]);
  if (error) throw error;
  return data ?? [];
}

export async function fetchCreatedEventsForUser(
  userId: string,
  tenantIds: readonly string[]
): Promise<Event[]> {
  if (tenantIds.length === 0) return [];

  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('created_by', userId)
    .in('tenant_id', [...tenantIds])
    .order('starts_at', { ascending: true });

  if (error) throw error;
  return data ?? [];
}

export async function fetchTenantEvents(tenantId: string): Promise<Event[]> {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('starts_at', { ascending: true });

  if (error) {
    throw error;
  }
  return data ?? [];
}

export async function fetchEventById(id: string): Promise<Event | null> {
  const { data, error } = await supabase.from('events').select('*').eq('id', id).maybeSingle();
  if (error) {
    throw error;
  }
  return data;
}

export async function fetchMyRsvp(eventId: string, userId: string): Promise<EventRsvp | null> {
  const { data, error } = await supabase
    .from('event_rsvps')
    .select('*')
    .eq('event_id', eventId)
    .eq('user_id', userId)
    .maybeSingle();
  if (error) {
    throw error;
  }
  return data;
}

/** Create or revive the caller's RSVP. Capacity is enforced by a DB trigger. */
export async function rsvpToEvent(event: Pick<Event, 'id' | 'tenant_id'>): Promise<void> {
  if (!event.tenant_id) {
    throw new Error('Personal events do not support club attendance.');
  }
  const { error } = await supabase
    .from('event_rsvps')
    .upsert(
      { event_id: event.id, tenant_id: event.tenant_id, status: 'going' },
      { onConflict: 'event_id,user_id' }
    );
  if (error) {
    throw error;
  }
}

export async function cancelRsvp(eventId: string): Promise<void> {
  const { error } = await supabase
    .from('event_rsvps')
    .update({ status: 'cancelled' })
    .eq('event_id', eventId);
  if (error) {
    throw error;
  }
}
